import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad — Rescate de Pareja: Post-Crisis

Última actualización: Abril 2026

1. Información que recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son procesados por nuestro proveedor de pagos seguro.

2. Uso de la información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra digital
• Enviarte el material adquirido por correo electrónico
• Comunicarnos contigo en caso de problemas con tu pedido

3. Protección de datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Compartir información
No vendemos, intercambiamos ni transferimos tu información personal a terceros. La única excepción son los proveedores de servicios que nos ayudan a operar nuestro sitio y procesar pagos, quienes están obligados a mantener la confidencialidad.
`.trim();
const termsContent = `
Términos y Condiciones — Rescate de Pareja: Post-Crisis

Última actualización: Abril 2026

1. Aceptación de los términos
Al adquirir y utilizar este producto digital, aceptás estos términos y condiciones en su totalidad. Si no estás de acuerdo, no realices la compra.

2. Descripción del producto
"Rescate de Pareja: Post-Crisis" es un producto digital (eBook en formato PDF) que incluye guías, plantillas y herramientas para mejorar la comunicación en pareja. No constituye asesoramiento psicológico, terapéutico ni profesional.

3. Entrega del producto
El producto se entrega de forma digital e inmediata tras confirmar el pago. Recibirás un enlace de descarga y/o el material por correo electrónico.

4. Política de reembolso
Ofrecemos una garantía de 7 días. Si el contenido no te aporta valor, puedes solicitar la devolución total de tu dinero dentro de este plazo.

5. Uso permitido
El material es para uso personal. Queda prohibida la reproducción, distribución, reventa o publicación del contenido, total o parcial, sin autorización expresa.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="w-full bg-[#F5F0E8] py-20 border-t border-[#E8E0D4] pb-32 md:pb-20" data-id="element-51">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-52">
          <div className="mb-20" data-id="element-53">
            <h3 className="text-3xl md:text-4xl font-bold text-[#1B4B5A] mb-10 font-serif" data-id="element-54">
              ¿Listo para empezar el rescate?
            </h3>
            <a href="#cta" className="inline-block bg-[#4fc251] text-white px-10 py-5 rounded-lg text-lg font-semibold shadow-lg hover:bg-[#A85D3F] transition-colors font-sans tracking-wide" data-id="element-55">
              SI, QUIERO ACCEDER AL MATERIAL
            </a>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 border-t border-[#E8E0D4]" data-id="element-56">
            <p className="text-[#7A7A6E] text-sm font-sans" data-id="element-57">
              © 2026 Rescate de Pareja: Post-Crisis. Todos los derechos
              reservados.
            </p>
            <div className="flex items-center gap-6" data-id="element-58">
              <button onClick={() => setActiveModal('privacy')} className="text-[#7A7A6E] hover:text-[#1B4B5A] text-sm transition-colors font-sans" data-id="element-59">
                Política de Privacidad
              </button>
              <button onClick={() => setActiveModal('terms')} className="text-[#7A7A6E] hover:text-[#1B4B5A] text-sm transition-colors font-sans" data-id="element-60">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-61">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-62">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-[#FAF7F2] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-63">
              <div className="flex items-center justify-between p-6 border-b border-[#E8E0D4]" data-id="element-64">
                <h3 className="text-xl font-bold text-[#1B4B5A] font-serif" data-id="element-65">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full bg-[#F5F0E8] flex items-center justify-center hover:bg-[#E8E0D4] transition-colors" data-id="element-66">
                  <XIcon className="w-5 h-5 text-[#1B4B5A]" data-id="element-67" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-68">
                <div className="prose prose-sm max-w-none font-sans" data-id="element-69">
                  {(activeModal === 'privacy' ? privacyContent : termsContent).split('\n').map((line, i) => {
                if (!line.trim()) return <div key={i} className="h-3" data-id="element-70" />;
                if (/^\d+\./.test(line.trim())) {
                  return <h4 key={i} className="text-lg font-bold text-[#2C2C2C] mt-6 mb-2" data-id="element-71">
                            {line}
                          </h4>;
                }
                if (line.startsWith('Política') || line.startsWith('Términos')) {
                  return <h4 key={i} className="text-lg font-bold text-[#1B4B5A] mb-1" data-id="element-72">
                            {line}
                          </h4>;
                }
                if (line.startsWith('Última')) {
                  return <p key={i} className="text-sm text-[#7A7A6E] mb-4" data-id="element-73">
                            {line}
                          </p>;
                }
                if (line.startsWith('•')) {
                  return <p key={i} className="text-[#2C2C2C] pl-4 mb-1" data-id="element-74">
                            {line}
                          </p>;
                }
                return <p key={i} className="text-[#2C2C2C] mb-2 leading-relaxed" data-id="element-75">
                          {line}
                        </p>;
              })}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}